'use client';

import React, { useState } from 'react';
import { apiClient } from '@/services/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Plus,
  Phone,
  Mail,
  MapPin,
  UserPlus,
  X,
  Link2,
  AlertTriangle,
  Loader2,
  Users,
} from 'lucide-react';
import {
  householdsAPI,
  HouseholdMember,
  HouseholdMemberAdd,
  HouseholdRole,
} from '@/services/api/households';
import { MembershipRoleFields, HOUSEHOLD_ROLE_LABELS } from './MembershipRoleFields';

const ROLE_LABELS = HOUSEHOLD_ROLE_LABELS as Record<HouseholdRole, string>;

const ROLE_COLORS: Record<HouseholdRole, string> = {
  HEAD_OF_HOUSEHOLD: 'bg-indigo-100 text-indigo-800',
  SPOUSE: 'bg-purple-100 text-purple-800',
  CHILD: 'bg-blue-100 text-blue-800',
  DEPENDENT: 'bg-teal-100 text-teal-800',
  OTHER: 'bg-gray-100 text-gray-800',
};

interface Props {
  householdId: number;
  members: HouseholdMember[];
  onRefresh: () => void;
}

export default function HouseholdMembersTab({ householdId, members, onRefresh }: Props) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<HouseholdMemberAdd>({
    member_id: 0,
    role_in_household: 'OTHER',
    is_displaced: true,
  });
  const [saving, setSaving] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState<number | null>(null);
  const [pendingFamilyMemberId, setPendingFamilyMemberId] = useState<number | null>(null);

  const searchMembers = async (query: string) => {
    setMemberSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      // Unified search returns both members and family members in this CBO
      const response = await apiClient.get('/api/members/search/unified', {
        params: { q: query, limit: 20 },
      });
      const data = response.data || {};
      // Tag each result with source ('member' or 'family_member')
      const memberItems = (data.members || []).map((m: any) => ({ ...m, source: 'member' }));
      const familyItems = (data.family_members || []).map((f: any) => ({
        ...f,
        source: 'family_member',
      }));
      setSearchResults([...memberItems, ...familyItems]);
    } catch (err) {
      console.error('Member search failed:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const selectMember = (item: any) => {
    if (item.source === 'family_member') {
      // Stash the family member info — we'll promote it on save
      setAddForm((prev) => ({
        ...prev,
        member_id: 0,  // will be assigned after promote
        current_email: item.email,
        current_phone: item.phone || item.phone_no,
        current_address_line1: item.street_address,
        current_city: item.city,
        current_state: item.state,
        current_zip: item.zip_code,
      }));
      // Use special marker so handleAddMember knows to promote first
      setMemberSearch(`${item.first_name} ${item.last_name} (family of ${item.parent_first_name || ''} ${item.parent_last_name || ''})`);
      // Track family_member_id separately by stashing on form (using window for simplicity)
      setPendingFamilyMemberId(item.family_member_id);
    } else {
      setAddForm((prev) => ({
        ...prev,
        member_id: item.id,
        current_email: item.email,
        current_phone: item.phone_no || item.phone_number,
        current_address_line1: item.street_address,
        current_city: item.city,
        current_state: item.state,
        current_zip: item.zip_code,
      }));
      setMemberSearch(`${item.first_name} ${item.last_name}`);
      setPendingFamilyMemberId(null);
    }
    setSearchResults([]);
  };

  const handleAddMember = async () => {
    // Either we have a real member_id, or a pending family member to promote
    if (!addForm.member_id && !pendingFamilyMemberId) return;
    setSaving(true);
    try {
      let memberIdToAdd = addForm.member_id;
      // Promote family member to a full member if needed
      if (pendingFamilyMemberId) {
        const promoteResp = await apiClient.post(
          `/api/members/family-members/${pendingFamilyMemberId}/promote`
        );
        memberIdToAdd = promoteResp.data.member_id;
      }
      await householdsAPI.addMember(householdId, { ...addForm, member_id: memberIdToAdd });
      setShowAddForm(false);
      setAddForm({ member_id: 0, role_in_household: 'OTHER', is_displaced: true });
      setMemberSearch('');
      setPendingFamilyMemberId(null);
      onRefresh();
    } catch (err: any) {
      console.error('Failed to add member:', err);
      alert(err.response?.data?.detail || 'Failed to add member to household');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (hmId: number) => {
    if (!confirm('Remove this member from the household?')) return;
    try {
      await householdsAPI.removeMember(householdId, hmId);
      onRefresh();
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  const handleLinkFamily = async (hmId: number) => {
    setLinking(hmId);
    try {
      const result = await householdsAPI.linkMemberToFamily(householdId, hmId);
      if (result.status === 'already_linked') {
        alert('This member is already linked as a family member.');
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to link family member');
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          Household Members ({members.length})
        </h3>
        <Button size="sm" onClick={() => setShowAddForm(!showAddForm)} className="gap-1.5">
          <UserPlus className="w-4 h-4" />
          Add Member
        </Button>
      </div>

      {/* Add Member Form */}
      {showAddForm && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Add Member to Household</h4>
              <button onClick={() => setShowAddForm(false)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Member Search */}
            <div className="relative">
              <Label>Search Existing Members</Label>
              <Input
                value={memberSearch}
                onChange={(e) => searchMembers(e.target.value)}
                placeholder="Type name to search..."
              />
              {searching && (
                <div className="absolute right-3 top-8">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              )}
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map((item, idx) => {
                    const isFamilyMember = item.source === 'family_member';
                    const itemKey = isFamilyMember
                      ? `fm-${item.family_member_id}`
                      : `m-${item.id}`;
                    return (
                      <button
                        key={itemKey + '-' + idx}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                        onClick={() => selectMember(item)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {item.first_name} {item.last_name}
                            </span>
                            {isFamilyMember ? (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 whitespace-nowrap">
                                Family of {item.parent_first_name} {item.parent_last_name}
                              </span>
                            ) : (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                Member
                              </span>
                            )}
                          </div>
                          {item.email && (
                            <div className="text-xs text-gray-500 truncate">{item.email}</div>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {item.member_number || ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Role / relationship / displaced — shared with member-side dialog */}
            <MembershipRoleFields
              role={addForm.role_in_household}
              onRoleChange={(role) =>
                setAddForm((prev) => ({ ...prev, role_in_household: role as HouseholdRole }))
              }
              relationshipToHead={addForm.relationship_to_head || ''}
              onRelationshipChange={(rel) =>
                setAddForm((prev) => ({ ...prev, relationship_to_head: rel }))
              }
              isDisplaced={addForm.is_displaced ?? true}
              onDisplacedChange={(d) =>
                setAddForm((prev) => ({ ...prev, is_displaced: d }))
              }
            />

            {/* Current Contact (only meaningful when displaced) */}
            <div>
              <Label className="text-xs text-gray-500">Current Contact (if displaced)</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <Input
                  value={addForm.current_phone || ''}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, current_phone: e.target.value }))}
                  placeholder="Phone"
                />
                <Input
                  value={addForm.current_email || ''}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, current_email: e.target.value }))}
                  placeholder="Email"
                />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-2">
                <Input
                  className="col-span-2"
                  value={addForm.current_address_line1 || ''}
                  onChange={(e) =>
                    setAddForm((prev) => ({ ...prev, current_address_line1: e.target.value }))
                  }
                  placeholder="Current Address"
                />
                <Input
                  value={addForm.current_city || ''}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, current_city: e.target.value }))}
                  placeholder="City"
                />
                <Input
                  value={addForm.current_state || ''}
                  onChange={(e) => setAddForm((prev) => ({ ...prev, current_state: e.target.value }))}
                  placeholder="State"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={(!addForm.member_id && !pendingFamilyMemberId) || saving} onClick={handleAddMember}>
                {saving ? 'Adding...' : 'Add to Household'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members List */}
      {members.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No members added yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {members.map((member) => (
            <Card key={member.id} className={member.status === 'INACTIVE' ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <button
                        className="font-medium text-gray-900 hover:text-indigo-600 hover:underline cursor-pointer"
                        onClick={() =>
                          window.dispatchEvent(
                            new CustomEvent('open-member', {
                              detail: { memberId: member.member_id },
                            })
                          )
                        }
                      >
                        {member.first_name} {member.last_name}
                      </button>
                      <Badge
                        className={ROLE_COLORS[member.role_in_household] || ROLE_COLORS.OTHER}
                        variant="secondary"
                      >
                        {ROLE_LABELS[member.role_in_household] || member.role_in_household}
                      </Badge>
                      {member.relationship_to_head && (
                        <span className="text-xs text-gray-500">({member.relationship_to_head})</span>
                      )}
                      {member.is_displaced && (
                        <Badge className="bg-red-50 text-red-700" variant="secondary">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Displaced
                        </Badge>
                      )}
                      {member.status === 'INACTIVE' && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>

                    {/* Contact info */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                      {(member.current_phone || member.member_phone) && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {member.current_phone || member.member_phone}
                        </span>
                      )}
                      {(member.current_email || member.member_email) && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {member.current_email || member.member_email}
                        </span>
                      )}
                      {member.current_address_line1 && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {[member.current_address_line1, member.current_city, member.current_state]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {member.role_in_household !== 'HEAD_OF_HOUSEHOLD' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleLinkFamily(member.id)}
                        disabled={linking === member.id}
                        title="Link as family member of head of household"
                      >
                        {linking === member.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Link2 className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                    {member.status === 'ACTIVE' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
